import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthCard } from '@/layouts/AuthLayout'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'

interface LoginFormProps {
  email: string|null
  onSuccess?: () => void
}

interface LoginFormValues {
  email: string
  password: string
}

export function LoginForm({
  email,
  onSuccess,
  className,
  ...props
}: React.ComponentProps<'div'> & LoginFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<LoginFormValues>({
    defaultValues: {
      email: email || undefined,
    },
  })

  const { login, resendVerification } = useAuth({
    middleware: 'guest',
    tmp: 'LoginForm',
  })

  useEffect(() => {
    if (resendCountdown <= 0) return

    const interval = setInterval(() => {
      setResendCountdown((seconds) => Math.max(seconds - 1, 0))
    }, 1000)

    return () => clearInterval(interval)
  }, [resendCountdown > 0])

  const parseRetryAfter = (value: unknown): number => {
    const seconds = Number(value)
    return Number.isFinite(seconds) && seconds > 0 ? seconds : 120
  }

  const handleResendVerification = async () => {
    if (!unverifiedEmail || isResending || resendCountdown > 0) return

    setIsResending(true)

    return resendVerification({ email: unverifiedEmail })
      .then((response) => {
        setResendCountdown(parseRetryAfter(response?.headers?.['retry-after']))

        toast.success('Ověřovací e-mail byl odeslán', {
          description: 'Zkontrolujte svou e-mailovou schránku.',
          duration: 5000,
          closeButton: true,
        })
      })
      .catch((error) => {
        if (error?.response?.status === 429) {
          setResendCountdown(
            parseRetryAfter(error.response.headers?.['retry-after'])
          )

          toast.error('Ověřovací e-mail jsme odeslali nedávno', {
            description: 'Před dalším odesláním chvíli počkejte.',
            duration: 5000,
            closeButton: true,
          })

          return
        }

        console.error(error)

        toast.error('Ověřovací e-mail se nepodařilo odeslat', {
          duration: 5000,
          closeButton: true,
        })
      })
      .finally(() => {
        setIsResending(false)
      })
  }

  const onSubmit = async (values: LoginFormValues) => {
    if (isSubmitting) return
    if (Object.keys(errors).length > 0) return
    if (!values.email || !values.password) return

    setIsSubmitting(true)
    setUnverifiedEmail(null)

    // return signIn('credentials', {
    //   email: values.email,
    //   password: values.password,
    //   csrfToken: values.csrfToken,
    //   redirect: false,
    // })
    return login({
      values,
      setError: () => {},
    })
      .then((response) => {
        if (response?.status >= 400) {
          toast.error('Nesprávné přihlašovací údaje', {
            duration: 5000,
            closeButton: true,
          })
        }

        if (response?.status === 200) {
          // toast.success('Přihlášení proběhlo úspěšně', {
          //   duration: 5000,
          //   closeButton: true,
          // })

          onSuccess?.()
        }
      })
      .catch((error) => {
        if (error?.response?.data?.code === 40097) {
          setUnverifiedEmail(values.email)
          return
        }

        console.error(error)

        toast.error('Nesprávné přihlašovací údaje', {
          duration: 5000,
          closeButton: true,
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <div className={cn('flex w-full flex-col gap-4', className)} {...props}>
      {unverifiedEmail && (
        <Alert>
          <AlertTitle>Účet zatím není ověřený</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>Zkontrolujte e-mail, nebo si nechte poslat nový ověřovací odkaz.</p>
            <Button
              variant="outline"
              size="sm"
              disabled={isResending || resendCountdown > 0}
              onClick={handleResendVerification}
            >
              {resendCountdown > 0
                ? `Odeslat znovu za ${resendCountdown} s`
                : isResending
                  ? 'Odesíláme'
                  : 'Znovu odeslat ověřovací e-mail'}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <AuthCard title="Přihlášení" description="Vítejte zpět. Přihlaste se ke svému účtu.">
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-5" noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor="email" className="text-[13px]">E-mail</Label>
            <Input
              aria-invalid={!!errors.email}
              id="email"
              type="email"
              placeholder="jmeno@firma.cz"
              disabled={isSubmitting}
              autoComplete="email"
              autoFocus
              {...register('email', {
                required: 'Zadejte prosím e-mail',
              })}
            />
            {errors.email && <p className="text-xs text-danger">{errors.email.message}</p>}
          </div>

          <div className="grid gap-1.5">
            <div className="flex items-center">
              <Label htmlFor="password" className="text-[13px]">Heslo</Label>
              <Link
                to="/auth/forgot-password"
                className="ml-auto text-[13px] text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Zapomněli jste heslo?
              </Link>
            </div>
            <Input
              aria-invalid={!!errors.password}
              id="password"
              type="password"
              disabled={isSubmitting}
              autoComplete="current-password"
              {...register('password', {
                required: 'Zadejte prosím heslo',
              })}
            />
            {errors.password && <p className="text-xs text-danger">{errors.password.message}</p>}
          </div>

          <Button className="mt-1 w-full" disabled={isSubmitting} type="submit">
            {isSubmitting && <Loader2 className="animate-spin" />}
            {isSubmitting ? 'Přihlašujeme' : 'Přihlásit se'}
          </Button>
        </form>
      </AuthCard>

      <p className="text-center text-sm text-muted-foreground">
        Nemáte účet?{' '}
        <Link to="/auth/signup" className="font-medium text-foreground underline-offset-4 hover:underline">
          Zaregistrujte se
        </Link>
      </p>
    </div>
  )
}
